import React, { useState, useRef, useEffect } from "react";
import {
  View,
  ScrollView,
  Text,
  Animated,
  PanResponder,
  TouchableOpacity,
  TextInput,
} from "react-native";
import { useTranslation } from "react-i18next";
import Ionicons from "@expo/vector-icons/Ionicons";
import ChatMessage from "../Firestore/ChatMessage";

import GlobalStyles from "../../Resources/Styles";
import Colors from "../../Resources/Color";
import Fonts from "../../Resources/Fonts";

import { Observer, observer } from "mobx-react";
import { audioFormatTime } from "../utils/numberUtils";
import ImageComponent from "../Components/ImageComponent";
import AudioManager from "../Classes/AudioManager";
import MediaManager from "../Classes/MediaManager";
import ChatManager from "../Classes/ChatManager";
import { runInAction } from "mobx";
import NetworkManager from "../Classes/NetworkManager";
import { useDevice } from "../_layout";

const ChatInputView = observer(({ client, fullScreen = false }) => {
  const { t } = useTranslation();
  const { isMobile } = useDevice();
  const [selectedImage, setSelectedImage] = useState(null);
  const inputRef = useRef(null);

  const chatManager = ChatManager.getInstance();
  const audioManager = AudioManager.getInstance();
  const mediaManager = MediaManager.getInstance();

  const sendMessage = async () => {
    if (client.chatText.trim() === "" && !selectedImage) return;

    // Cache and clear the reply-to ID.
    const replyTo = chatManager.selectedReplyToId;
    if (replyTo) {
      chatManager.setProperty("selectedReplyToId", null);
    }
    const networkManager = NetworkManager.getInstance();
    const timestamp = networkManager.getNetworkTime();

    if (selectedImage) {
      try {
        const { uri, file } = await mediaManager.compressMediaFileWeb(
          selectedImage,
          Date.now().toString(),
          "image"
        );

        if (uri) {
          const newMessage = new ChatMessage(
            undefined,
            timestamp,
            client.chatText.trim(),
            "image",
            true,
            replyTo ? replyTo : null,
            file,
            undefined,
            undefined,
            undefined,
            undefined,
            client,
            undefined,
            selectedImage
          );
          runInAction(() => {
            client.messages.push(newMessage);
          });

          chatManager.scrollToBottom();

          client.uploadMessageAndAttachment(newMessage);
        }
      } catch (error) {
        console.error("Error saving or uploading image:", error);
      }
    } else {
      // For text-only messages.

      const newMessage = new ChatMessage(
        undefined,
        timestamp,
        client.chatText.trim(),
        "text",
        true,
        replyTo ? replyTo : null,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        client,
        undefined
      );

      runInAction(() => {
        client.messages.push(newMessage);
      });
      chatManager.scrollToBottom();

      client.uploadMessageAndAttachment(newMessage);
    }

    // Reset the text ref.
    // setText("");
    client.setProperty("chatText", "");

    setSelectedImage(null);
  };

  const SendMessageButton = () => {
    return (
      <TouchableOpacity
        style={{
          alignItems: "center",
          justifyContent: "center",
          paddingVertical: 6,
          borderRadius: 14,
          backgroundColor: Colors.messageBubbleColor,
          width: 54,
          height: isMobile ? 36 : 34,
        }}
        onPress={sendMessage}
      >
        <Ionicons
          name="paper-plane-sharp"
          size={20}
          color={Colors.whiteColor}
        />
      </TouchableOpacity>
    );
  };

  const pickImage = async () => {
    const result = await mediaManager.loadImage();

    if (!result?.cancelled && result?.uri) {
      setSelectedImage(result.uri);
    }
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      // if user types the special literal 'scape', show a temporary placeholder
      if (
        typeof e.key === "string" &&
        e.key === "Escape" &&
        !audioManager.isRecording
      ) {
        client.coach.setProperty("selectedChatId", "");
        chatManager.setProperty("selectedReplyToId", null);
        mediaManager.closeMediaPlayer();
        return;
      }
      // only react to “real” characters (not Shift, Ctrl, etc.)
      if (
        e.key.length === 1 &&
        inputRef.current &&
        !inputRef.current.isFocused()
      ) {
        inputRef.current.focus();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  useEffect(() => {
    if (chatManager.selectedReplyToId != null) {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }
    if (client && inputRef.current && !isMobile) {
      inputRef.current.focus();
    }
  }, [chatManager.selectedReplyToId, client]);

  return (
    <Animated.View style={{ zIndex: 1 }}>
      <View
        style={{
          backgroundColor: Colors.lightGrayColor,
          borderTopColor: Colors.borderColor,
          borderTopWidth: 0.1,
          paddingHorizontal: fullScreen ? 4 : 0,
        }}
      >
        {/* Reply to Message */}
        {chatManager.selectedReplyToId && (
          <ReplyToMessageView client={client} />
        )}

        <View
          style={{
            flexDirection: "row",
            width: "100%",
          }}
        >
          <View
            style={{
              flexDirection: "row",
              alignItems: "flex-end",
              paddingVertical: 8,
              paddingHorizontal: 10,
              flex: 1,
            }}
          >
            {/* Selected Image Preview */}
            {selectedImage && (
              <View
                style={{
                  position: "relative",
                  maxWidth: isMobile ? 36 : 34,
                  maxHeight: isMobile ? 36 : 34,
                  marginRight: 8,
                  alignItems: "center",
                }}
              >
                <View
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 10,
                  }}
                >
                  <TouchableOpacity
                    onPress={() => {
                      setSelectedImage(null);
                    }}
                    style={{
                      flex: 1,
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Ionicons
                      name="trash-outline"
                      size={20}
                      color={Colors.blackColor}
                    />
                  </TouchableOpacity>
                </View>
                <View
                  style={{
                    opacity: 0.5,
                    maxWidth: 40,
                    overflow: "hidden",
                    borderRadius: 8,
                    alignSelf: "center",
                    alignItems: "center",
                    flexShrink: 1,
                    minWidth: 24,
                  }}
                >
                  <ImageComponent
                    imageURL={selectedImage}
                    height="100%"
                    small
                  />
                </View>
              </View>
            )}
            {/* Text Input */}
            <View
              style={{
                flex: 1,
                marginRight: 8,
                borderRadius: 14,
                borderWidth: 1,
                borderColor: Colors.borderColor,
                backgroundColor: Colors.whiteColor,
                minHeight: 34,
                maxHeight: 107,
                flexShrink: 1,
                overflow: "hidden",
              }}
            >
              <TextInput
                ref={inputRef}
                value={client.chatText}
                onChangeText={(newText) => {
                  if (!audioManager.isRecording) {
                    client.setProperty("chatText", newText);
                  }
                }}
                placeholder={t("messagePlaceholder-string")}
                placeholderTextColor={Colors.grayColor}
                multiline={true}
                style={[
                  Fonts.regular14,
                  {
                    fontSize: isMobile ? 16 : 14,
                    color: Colors.blackColor,
                    outlineStyle: "none",
                    textAlignVertical: "top",
                    position: "absolute",
                    width: "100%",
                    height: "100%",
                    paddingVertical: 8,
                    paddingHorizontal: 8,
                  },
                ]}
                maxLength={700}
                autoCorrect={false}
                blurOnSubmit={false}
                onKeyPress={({ nativeEvent }) => {
                  if (nativeEvent.key === "Enter") {
                    if (nativeEvent.shiftKey) {
                      // Shift+Enter → insert newline (do nothing, allow it)
                      return;
                    }

                    // Prevent default newline
                    nativeEvent.preventDefault?.();

                    // Send message
                    sendMessage();

                    // Re-focus the input after sending
                    setTimeout(() => {
                      inputRef.current?.focus();
                      console.log("focused");
                    }, 80);
                  } else if (
                    nativeEvent.key === "Escape" &&
                    !audioManager.isRecording &&
                    !client.chatText
                  ) {
                    client.coach.setProperty("selectedChatId", "");
                    chatManager.setProperty("selectedReplyToId", null);
                    mediaManager.closeMediaPlayer();
                  }
                }}
                showsVerticalScrollIndicator={false}
              />
              <View
                style={{
                  position: "relative",
                  zIndex: -2,
                  paddingVertical: 8,
                  paddingHorizontal: 8,
                }}
              >
                <Text
                  style={{
                    fontSize: isMobile ? 16 : 14,
                    color: "transparent",
                    flexWrap: "wrap", // forces wrapping
                    // width: "100%", // define the wrapping container width
                  }}
                  numberOfLines={undefined}
                >
                  {client.chatText || " "}
                </Text>
              </View>
            </View>

            {/* Send Button or Image Picker */}
            {client.chatText.trim() !== "" || selectedImage !== null ? (
              <SendMessageButton />
            ) : (
              <>
                <TouchableOpacity
                  onPress={pickImage}
                  style={{
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                    // flex: 1,
                  }}
                >
                  <Ionicons
                    name="image-outline"
                    size={24}
                    color={Colors.blackColor}
                    style={{ paddingLeft: 10 }}
                  />
                </TouchableOpacity>
              </>
            )}
          </View>

          {audioManager.isRecording && (
            <View
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 40,
                bottom: 0,
                backgroundColor: Colors.lightGrayColor,
                zIndex: audioManager.isRecording ? 10 : 0,
              }}
            >
              <RecordingInfoView />
            </View>
          )}
          {!(client.chatText.trim() !== "" || selectedImage !== null) && (
            <DraggableMicrophone client={client} />
          )}
        </View>
      </View>
    </Animated.View>
  );
});

const ReplyToMessageView = ({ client }) => {
  const chatManager = ChatManager.getInstance();
  const { t } = useTranslation();

  const replyToMessage = client.messages.find(
    (msg) => msg.id === chatManager.selectedReplyToId
  );

  if (!replyToMessage) return null;

  return (
    <View
      style={{
        flexDirection: "row",
        alignItems: "center",
        height: 40,
        backgroundColor: Colors.lightGrayColor,
        width: "100%",
      }}
    >
      {/* Left Indicator */}
      <View
        style={{
          width: 4,
          height: 36,
          backgroundColor: "#808080",
          borderRadius: 2,
        }}
      />

      {/* Reply Message Info */}
      <View style={{ flex: 1, paddingHorizontal: 8 }}>
        <Text
          style={{
            fontSize: 14,
            fontWeight: "600",
            color: "#808080",
            marginBottom: 1,
          }}
        >
          {replyToMessage.isFromCoach ? "Coach" : "You"}
        </Text>

        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
          }}
        >
          {/* Audio Message */}
          {replyToMessage.type === "audio" && (
            <>
              <Ionicons
                name="mic-outline"
                size={12}
                color="gray"
                style={{ marginRight: 4 }}
              />
              <Text style={{ fontSize: 12, color: "gray", flexShrink: 1 }}>
                {audioFormatTime(replyToMessage.totalTime)}
              </Text>
            </>
          )}

          {/* Image Message */}
          {replyToMessage.type === "image" && (
            <>
              <Ionicons
                name="camera-outline"
                size={12}
                color="gray"
                style={{ marginRight: 4 }}
              />
              <Text
                style={{
                  fontSize: 12,
                  color: "gray",
                  flexShrink: 1,
                  width: "100%",
                }}
                numberOfLines={1}
              >
                {replyToMessage.text || t("photo-string")}
              </Text>
            </>
          )}

          {/* Video Message */}
          {replyToMessage.type === "video" && (
            <>
              <Ionicons
                name="videocam-outline"
                size={12}
                color="gray"
                style={{ marginRight: 4 }}
              />
              <Text style={{ fontSize: 12, color: "gray", flexShrink: 1 }}>
                {replyToMessage.totalTime
                  ? `${t("video-string")} - ${Math.floor(
                      replyToMessage.totalTime
                    )}s`
                  : t("video-string")}
              </Text>
            </>
          )}

          {/* Text Message */}
          {replyToMessage.type === "text" && (
            <Text
              style={{
                fontSize: 12,
                color: "gray",
                flexShrink: 1,
                width: "100%",
              }}
              numberOfLines={1}
            >
              {replyToMessage.text || t("photo-string")}
            </Text>
          )}
        </View>
      </View>

      {/* Close Button */}
      <TouchableOpacity
        onPress={() => {
          chatManager.setProperty("selectedReplyToId", null);
        }}
      >
        <Ionicons
          name="close-circle"
          size={20}
          color="gray"
          style={{ paddingRight: 8 }}
        />
      </TouchableOpacity>
    </View>
  );
};

const DraggableMicrophone = observer(({ client }) => {
  const audioManager = AudioManager.getInstance();
  const isRecording = audioManager.isRecording;

  /* ANIMATIONS */
  const animatedScale = useRef(new Animated.Value(1)).current;
  const lockedScale = useRef(new Animated.Value(1)).current;

  // Animate the outer pulsating circle.
  const pulsate = () => {
    Animated.loop(
      Animated.sequence([
        Animated.timing(animatedScale, {
          toValue: 1.25,
          duration: 500,
          useNativeDriver: true,
        }),
        Animated.timing(animatedScale, {
          toValue: 1,
          duration: 500,
          useNativeDriver: true,
        }),
      ])
    ).start();
  };

  // Animate locked indicator scale when locked.
  useEffect(() => {
    if (isRecording) {
      Animated.loop(
        Animated.sequence([
          Animated.timing(lockedScale, {
            toValue: 1.2,
            duration: 500,
            useNativeDriver: true,
          }),
          Animated.timing(lockedScale, {
            toValue: 1,
            duration: 500,
            useNativeDriver: true,
          }),
        ])
      ).start();
    } else {
      lockedScale.setValue(1);
    }
  }, [isRecording, lockedScale]);

  return (
    <View
      style={{
        alignItems: "center",
        justifyContent: "center",
        paddingRight: isRecording ? 10 : 8,
        marginLeft: -2,
        zIndex: 11,
      }}
    >
      {/* Outer container for fixed circle & pulsating circle */}
      <View
        style={{
          position: "relative",
          width: 34,
          height: 34,
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {isRecording && (
          <Animated.View
            style={{
              position: "absolute",
              width: 34,
              height: 34,
              borderRadius: 20,
              backgroundColor: "red",
              opacity: 0.3,
              transform: [{ scale: animatedScale }],
              zIndex: -1,
            }}
          />
        )}

        <TouchableOpacity
          activeOpacity={1}
          style={{
            width: "100%",
            height: "100%",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2,
            alignSelf: "center",
          }}
          onPress={() => {
            if (!isRecording) {
              audioManager.startRecording();
              pulsate();
            } else {
              audioManager.stopRecording(client);
            }
          }}
        >
          <View
            style={{
              width: 34,
              height: 34,
              borderRadius: 20,
              backgroundColor: isRecording ? "rgb(181, 50, 40)" : "transparent",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 0,
              paddingTop: isRecording ? 2 : 0,
              paddingRight: isRecording ? 2 : 0,
            }}
          >
            <Ionicons
              name={isRecording ? "paper-plane" : "mic"}
              size={24}
              color={isRecording ? Colors.whiteColor : Colors.blackColor}
              style={{ opacity: 1, zIndex: 2 }}
            />
          </View>
        </TouchableOpacity>
      </View>
    </View>
  );
});

const RecordingInfoView = observer(() => {
  const audioManager = AudioManager.getInstance();
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollToEnd({ animated: true });
    }
  }, [audioManager.soundSamples.length]);

  return (
    <View
      style={{
        flexDirection: "row",
        alignItems: "center",
        flexGrow: 1,
        paddingHorizontal: 16,
        height: 56,
      }}
    >
      {/* Trash Button */}
      <TouchableOpacity
        onPress={() => audioManager.deleteRecording()}
        style={{
          marginRight: 8,
          marginBottom: 3,
        }}
      >
        <Ionicons name="trash-outline" size={24} color={Colors.redColor} />
      </TouchableOpacity>

      <Text
        style={{
          fontSize: 18,
          color: Colors.blackColor,
          marginLeft: 8,
          marginBottom: 3,
        }}
      >
        {`${Math.floor(audioManager.recordingTime / 60)}:${String(
          Math.floor(audioManager.recordingTime % 60)
        ).padStart(2, "0")}`}
      </Text>
      <Observer>
        {() => (
          <View style={{ flex: 1, overflow: "hidden", marginLeft: 8 }}>
            <ScrollView
              ref={scrollRef}
              horizontal
              showsHorizontalScrollIndicator={false}
              style={{ width: "100%", paddingHorizontal: 4 }}
              contentContainerStyle={{
                flexDirection: "row",
                alignItems: "center",
              }}
            >
              {audioManager.soundSamples.map((sample, index) => (
                <View
                  key={index}
                  style={{
                    width: 3,
                    height: Math.max(5, sample * 28),
                    backgroundColor: Colors.blackColor,
                    borderRadius: 2,
                    marginHorizontal: 1,
                  }}
                />
              ))}
            </ScrollView>
          </View>
        )}
      </Observer>
    </View>
  );
});

export default ChatInputView;
